import { useQuery } from "@tanstack/react-query";
import type { Trip } from "@travelix/core";
import { fetchTripWeather, type WeatherByDate } from "@/lib/weather";

/**
 * Weather-by-date for a trip's first destination. Silently returns {} on
 * any failure (network, geocoding miss, dates beyond forecast horizon).
 * Cached in localStorage inside fetchTripWeather; TanStack Query gives us
 * an extra in-memory cache for the current session.
 */
export function useTripWeather(trip: Trip | undefined | null) {
  const destination = trip?.destinations?.[0] ?? "";
  const startDate = trip ? toYmd(trip.startDate.toDate()) : "";
  const endDate = trip ? toYmd(trip.endDate.toDate()) : "";
  const key = trip ? ["weather", trip.id, destination, startDate, endDate] : ["weather", "none"];

  return useQuery<WeatherByDate>({
    queryKey: key,
    enabled: !!trip && !!destination,
    staleTime: 60 * 60 * 1000, // 1h in-memory
    queryFn: () =>
      fetchTripWeather({
        tripId: trip!.id,
        destination,
        startDate,
        endDate,
      }),
  });
}

function toYmd(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
